import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, LayoutGrid, DollarSign, Hash, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SERVICE_OPTION_THEMES } from '@/components/ServiceOptionChip';

/**
 * ServiceOptionMixWidget — dashboard widget that surfaces the Reroof /
 * New Roof / Repairs / Restoration job mix month-over-month.
 *
 * Toggles between "Count" mode (number of jobs per month) and "Value"
 * mode ($ contract value per month). Each month renders as a stacked
 * bar split by service_option, using the canonical workflow colours
 * from ServiceOptionChip.
 *
 * Designed to answer the seasonal-planning question:
 *  "Are we mostly running re-roofs right now, or do we need painters
 *   for restoration season?"
 */

const OPTIONS = ['Reroof', 'New Roof', 'Repairs', 'Restoration'];

// Tailwind colours can't be applied via dynamic class names in
// Tailwind JIT mode, so we use inline hex colours that mirror
// SERVICE_OPTION_THEMES dots. Single source of truth lives in the
// chip component.
const OPTION_HEX = {
  'Reroof':       '#64748B',  // slate-500
  'New Roof':     '#10B981',  // emerald-500
  'Repairs':      '#F97316',  // orange-500
  'Restoration':  '#6366F1',  // indigo-500
};

export default function ServiceOptionMixWidget({ api }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('count');  // 'count' | 'value'

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/dashboard/service-option-mix?months=6')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  const max = useMemo(() => {
    if (!data) return 0;
    let m = 0;
    for (const month of data.months) {
      let stack = 0;
      for (const opt of OPTIONS) {
        const b = month.buckets[opt] || {};
        stack += mode === 'count' ? (b.count || 0) : (b.value || 0);
      }
      if (stack > m) m = stack;
    }
    return m;
  }, [data, mode]);

  const grandTotalCount = useMemo(() =>
    data ? OPTIONS.reduce((a, o) => a + (data.totals_count[o] || 0), 0) : 0
  , [data]);

  const grandTotalValue = useMemo(() =>
    data ? OPTIONS.reduce((a, o) => a + (data.totals_value[o] || 0), 0) : 0
  , [data]);

  if (loading) {
    return (
      <div data-testid="service-mix-widget-loading" className="bg-white border border-[#E2E2DF] rounded-md p-6">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin" /> Loading service-option mix…
        </div>
      </div>
    );
  }
  if (!data || data.months.length === 0) {
    return null;
  }

  // Pixel chart sizing — keep it compact (≈ a single-row widget)
  const CHART_H = 130;
  const COL_W = 56;
  const GAP = 12;
  const PAD = 14;
  const totalW = PAD * 2 + data.months.length * COL_W + (data.months.length - 1) * GAP;

  return (
    <div data-testid="service-mix-widget" className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
      {/* Header bar */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <LayoutGrid size={15} className="text-slate-500 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 font-[Manrope]">Service Mix · last {data.window_months}m</h3>
            <p className="text-[11px] text-slate-500 truncate">Workflow split for capacity planning</p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            data-testid="service-mix-mode-count"
            onClick={() => setMode('count')}
            className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors ${mode === 'count' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          >
            <Hash size={10} /> Count
          </button>
          <button
            data-testid="service-mix-mode-value"
            onClick={() => setMode('value')}
            className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors ${mode === 'value' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          >
            <DollarSign size={10} /> Value
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4 overflow-x-auto">
        <svg width={totalW} height={CHART_H + 30} className="block" data-testid="service-mix-chart">
          {/* Stacked bars */}
          {data.months.map((mo, i) => {
            const x = PAD + i * (COL_W + GAP);
            let cursorY = CHART_H;
            const monthTotal = OPTIONS.reduce((a, o) => a + (mode === 'count' ? (mo.buckets[o]?.count || 0) : (mo.buckets[o]?.value || 0)), 0);
            return (
              <g key={mo.month} data-testid={`service-mix-bar-${mo.month}`}>
                {OPTIONS.map(opt => {
                  const v = mode === 'count' ? (mo.buckets[opt]?.count || 0) : (mo.buckets[opt]?.value || 0);
                  if (!v || !max) return null;
                  const h = (v / max) * CHART_H;
                  cursorY -= h;
                  return (
                    <rect
                      key={opt}
                      x={x}
                      y={cursorY}
                      width={COL_W}
                      height={h}
                      fill={OPTION_HEX[opt]}
                      className="hover:opacity-80 cursor-pointer transition-opacity"
                      onClick={() => navigate(`/pipeline/stage/1?service_option=${encodeURIComponent(opt)}`)}
                    >
                      <title>{opt} · {mode === 'count' ? `${v} jobs` : `$${v.toLocaleString()}`} · {mo.label}</title>
                    </rect>
                  );
                })}
                {/* X-axis label */}
                <text
                  x={x + COL_W / 2}
                  y={CHART_H + 14}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#64748B"
                  fontWeight="600"
                >
                  {mo.label.slice(0, 3)}
                </text>
                <text
                  x={x + COL_W / 2}
                  y={CHART_H + 26}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#94A3B8"
                  fontFamily="monospace"
                >
                  {mode === 'count'
                    ? (monthTotal || 0).toLocaleString()
                    : monthTotal > 0 ? `$${Math.round(monthTotal / 1000)}k` : '—'}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend + totals */}
      <div className="px-4 pb-3 flex items-center gap-4 flex-wrap border-t border-slate-100 pt-2.5">
        {OPTIONS.map(opt => {
          const t = SERVICE_OPTION_THEMES[opt];
          const count = data.totals_count[opt] || 0;
          const value = data.totals_value[opt] || 0;
          const share = grandTotalCount > 0 ? Math.round((count / grandTotalCount) * 100) : 0;
          return (
            <div key={opt} data-testid={`service-mix-legend-${opt.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-1.5 min-w-0">
              <span className={`w-2 h-2 rounded-full ${t.dot} flex-shrink-0`} />
              <span className="text-[11px] font-bold text-slate-700">{opt}</span>
              <span className="text-[10px] text-slate-500 font-mono-num">
                {mode === 'count'
                  ? `${count} · ${share}%`
                  : `$${(value / 1000).toFixed(1)}k`}
              </span>
            </div>
          );
        })}
        <div className="ml-auto text-[10px] text-slate-400 flex items-center gap-1">
          {mode === 'count'
            ? `Total: ${grandTotalCount.toLocaleString()} jobs`
            : `Total: $${(grandTotalValue / 1000).toFixed(1)}k`}
          <ArrowUpRight size={10} />
        </div>
      </div>
    </div>
  );
}
